import React, { useState, useRef, useCallback, useEffect } from 'react'
import { IconButton, Tooltip, Typography, LinearProgress } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CloseIcon from '@material-ui/icons/Close'
import axios from 'axios'
import UploadCategoryWizard from './UploadCategoryWizard'

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(46, 125, 50, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  transition: 'opacity 0.2s ease'
}

const overlayTextStyle = {
  color: '#fff',
  fontSize: 28,
  fontWeight: 600,
  marginTop: 16
}

const overlaySubTextStyle = {
  color: 'rgba(255,255,255,0.8)',
  fontSize: 14,
  marginTop: 8
}

const overlayIconStyle = {
  color: '#fff',
  fontSize: 64
}

// ── Background progress pill ─────────────────────────────────────
// Fixed to bottom-right, shows upload + parse progress, auto-dismisses.
const progressPillStyle = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1400,
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  padding: '12px 16px',
  minWidth: 300,
  maxWidth: 400
}

const emitDocumentUploaded = (projectId, payload) => {
  if (!projectId || !payload || typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new CustomEvent('bid-estimation-document-uploaded', {
    detail: { projectId: String(projectId), payload }
  }))
}

const UploadDropzone = React.forwardRef(({ projectId, url, onUploadComplete }, ref) => {
  const [dragOver, setDragOver] = useState(false)
  const [wizardFile, setWizardFile] = useState(null)
  const [fileQueue, setFileQueue] = useState([]) // remaining files to classify after current
  const fileInputRef = useRef()
  const dragCounterRef = useRef(0)

  // Background upload state
  const [bgJob, setBgJob] = useState(null)
  // bgJob shape: { fileName, category, status, progress, error, result }
  // status: 'uploading' | 'analyzing' | 'classified' | 'done' | 'error'

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) setDragOver(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Start classifying the first file, queue the rest
  const startFileQueue = useCallback((files) => {
    if (!files || files.length === 0) return
    const arr = Array.from(files)
    setWizardFile(arr[0])
    setFileQueue(arr.slice(1))
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    dragCounterRef.current = 0
    const files = e.dataTransfer?.files
    if (files && files.length > 0) startFileQueue(files)
  }, [startFileQueue])

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  // Expose triggerUpload so parent/sibling components can open the file picker
  React.useImperativeHandle(ref, () => ({
    triggerUpload: handleButtonClick
  }))

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) startFileQueue(files)
    e.target.value = ''
  }

  // Advance to next file in queue, or close wizard
  const advanceQueue = useCallback(() => {
    if (fileQueue.length > 0) {
      setWizardFile(fileQueue[0])
      setFileQueue(prev => prev.slice(1))
    } else {
      setWizardFile(null)
    }
  }, [fileQueue])

  const handleWizardClose = () => {
    // Skip this file and advance to next
    advanceQueue()
  }

  // Called when user picks a category — wizard closes, upload starts in background
  const handleCategoryConfirmed = (category) => {
    const file = wizardFile
    advanceQueue() // Close wizard and show next file (or close)

    setBgJob({
      fileName: file.name,
      category,
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
      startTime: Date.now()
    })

    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', category)

    axios.post(
      `${url || ''}/api/bid-estimation/projects/${projectId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100)
            setBgJob(prev => prev ? { ...prev, progress: pct, status: pct < 100 ? 'uploading' : 'analyzing' } : prev)
          }
        }
      }
    ).then(({ data }) => {
      // Plans: classified → show page selector
      if (category === 'plans' && data.parseStatus === 'classified' && data.parseResult?.documentType === 'plan_set') {
        emitDocumentUploaded(projectId, data)
        setBgJob(null)
        if (onUploadComplete) onUploadComplete({ ...data, category })
        return
      }

      // Plans: small set fully parsed
      if (category === 'plans' && data.parseStatus === 'parsed' && data.parseResult?.documentType === 'plan_set') {
        emitDocumentUploaded(projectId, data)
        setBgJob(null)
        if (onUploadComplete) onUploadComplete({ ...data, category })
        return
      }

      // Vendor quote
      if (category === 'vendor_quote' && data.vendorQuote) {
        emitDocumentUploaded(projectId, data)
        setBgJob(null)
        if (onUploadComplete) onUploadComplete({ ...data, category, action: 'open_quote_review' })
        return
      }

      // Bid responses return a BidPackage, not a project document. Let the
      // dashboard refresh bid packages instead of appending it to Documents.
      if (category === 'bid_response') {
        const bidPackageId = data.bidPackageId || data.packageId || data._id
        setBgJob(null)
        if (onUploadComplete) {
          onUploadComplete({ ...data, category, action: 'open_bid_response', bidPackageId })
        }
        return
      }

      // Everything else
      emitDocumentUploaded(projectId, data)
      setBgJob(null)
      if (onUploadComplete) onUploadComplete({ ...data, category })
    }).catch((err) => {
      setBgJob(prev => prev ? {
        ...prev,
        status: 'error',
        error: err.response?.data?.error || err.message || 'Upload failed'
      } : prev)
    })
  }

  const handleParseStarted = ({ docId, pages, message }) => {
    setBgJob(prev => prev ? {
      ...prev,
      status: 'analyzing',
      result: prev.result || { _id: docId },
      progressMessage: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    } : {
      fileName: 'Plan set',
      category: 'plans',
      status: 'analyzing',
      progress: 100,
      error: null,
      result: { _id: docId },
      progressMessage: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    })
  }

  // Attach drag listeners to the whole document
  React.useEffect(() => {
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)
    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  const statusLabel = bgJob ? {
    uploading: `Uploading ${bgJob.fileName}…`,
    analyzing: bgJob.progressMessage || bgJob.message || `Analyzing ${bgJob.fileName}…`,
    classified: bgJob.message || 'Plan set classified',
    done: bgJob.message || 'Upload complete',
    error: bgJob.error || 'Upload failed'
  }[bgJob.status] : ''

  return (
    <>
      {/* Upload button for the header */}
      <Tooltip title="Upload File">
        <IconButton
          size="small"
          onClick={handleButtonClick}
          style={{ color: '#fff' }}
        >
          <CloudUploadIcon />
        </IconButton>
      </Tooltip>

      {/* Hidden file input — supports multiple files */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Drag overlay */}
      {dragOver && (
        <div style={overlayStyle}>
          <CloudUploadIcon style={overlayIconStyle} />
          <div style={overlayTextStyle}>Drop to Upload</div>
          <div style={overlaySubTextStyle}>Release to upload your file to this project</div>
        </div>
      )}

      {/* Category wizard — only picks category, no upload */}
      {wizardFile && (
        <UploadCategoryWizard
          file={wizardFile}
          remainingCount={fileQueue.length}
          onClose={handleWizardClose}
          onCategoryConfirmed={handleCategoryConfirmed}
        />
      )}

      {/* Upload progress only; background parsing is owned by DocumentsSection job tray */}
      {bgJob && (bgJob.status === 'uploading' || bgJob.status === 'error') && (
        <div style={progressPillStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: bgJob.status === 'uploading' ? 8 : 0 }}>
            {bgJob.status === 'error' && <ErrorOutlineIcon style={{ color: '#c62828', fontSize: 20 }} />}
            {bgJob.status === 'uploading' && (
              <CloudUploadIcon style={{ color: '#1976d2', fontSize: 20 }} />
            )}

            <span style={{
              flex: 1,
              fontSize: 13,
              fontWeight: 500,
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {statusLabel}
            </span>

            {/* Dismiss button */}
            {bgJob.status === 'error' && (
              <IconButton size="small" onClick={() => setBgJob(null)} style={{ padding: 2 }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>

          {/* Progress bar for upload */}
          {bgJob.status === 'uploading' && (
            <LinearProgress
              variant='determinate'
              value={bgJob.progress}
              style={{ height: 4, borderRadius: 2 }}
            />
          )}
        </div>
      )}
    </>
  )
})

export default UploadDropzone
